import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config/api";

function ExperienceAdmin() {
  const [experiences, setExperiences] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    company: "",
    location: "",
    period: "",
    description: [],
    technologies: [],
    order: 0,
  });
  const [descInput, setDescInput] = useState("");
  const [techInput, setTechInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    try {
      const response = await axios.get(`${API_URL}/experience`);
      if (response.data.success) {
        setExperiences(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching experiences:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      company: "",
      location: "",
      period: "",
      description: [],
      technologies: [],
      order: 0,
    });
    setEditingId(null);
    setTechInput("");
    setDescInput("");
  };

  const addDescription = () => {
    if (descInput.trim() && !formData.description.includes(descInput.trim())) {
      setFormData({
        ...formData,
        description: [...formData.description, descInput.trim()],
      });
      setDescInput("");
    }
  };

  const removeDescription = (index) => {
    setFormData({
      ...formData,
      description: formData.description.filter((_, i) => i !== index),
    });
  };

  const handleEdit = (exp) => {
    setFormData({
      title: exp.title || "",
      company: exp.company || "",
      location: exp.location || "",
      period: exp.period || "",
      description: exp.description || [],
      technologies: exp.technologies || [],
      order: exp.order || 0,
    });
    setEditingId(exp._id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const token = localStorage.getItem("adminToken");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingId) {
        await axios.put(`${API_URL}/experience/${editingId}`, formData, config);
        setMessage({
          type: "success",
          text: "Experience updated successfully!",
        });
      } else {
        await axios.post(`${API_URL}/experience`, formData, config);
        setMessage({
          type: "success",
          text: "Experience created successfully!",
        });
      }

      fetchExperiences();
      resetForm();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Operation failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this experience?"))
      return;

    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API_URL}/experience/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage({ type: "success", text: "Experience deleted successfully!" });
      fetchExperiences();
    } catch (error) {
      setMessage({ type: "error", text: "Delete failed" });
    }
  };

  const addTechnology = () => {
    if (techInput.trim() && !formData.technologies.includes(techInput.trim())) {
      setFormData({
        ...formData,
        technologies: [...formData.technologies, techInput.trim()],
      });
      setTechInput("");
    }
  };

  const removeTechnology = (index) => {
    setFormData({
      ...formData,
      technologies: formData.technologies.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <h2 className="text-xl sm:text-2xl font-light mb-4 sm:mb-6">
        Manage Experiences
      </h2>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="space-y-4 sm:space-y-6 bg-white/[0.03] border border-white/[0.08] rounded-lg sm:rounded-xl p-4 sm:p-6"
      >
        <h3 className="text-lg sm:text-xl font-light">
          {editingId ? "Edit Experience" : "Add New Experience"}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-white/60 text-sm mb-2">Company</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) =>
                setFormData({ ...formData, company: e.target.value })
              }
              className="w-full px-4 py-3 bg-white/[0.05] border border-white/[0.1] rounded-xl text-white
                       focus:outline-none focus:border-white/30 transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className="w-full px-4 py-3 bg-white/[0.05] border border-white/[0.1] rounded-xl text-white
                       focus:outline-none focus:border-white/30 transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
              placeholder="e.g., New York, NY"
              className="w-full px-4 py-3 bg-white/[0.05] border border-white/[0.1] rounded-xl text-white
                       focus:outline-none focus:border-white/30 transition-all"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-white/60 text-sm mb-2">Period</label>
            <input
              type="text"
              value={formData.period}
              onChange={(e) =>
                setFormData({ ...formData, period: e.target.value })
              }
              placeholder="e.g., Jan 2023 - Present"
              className="w-full px-4 py-3 bg-white/[0.05] border border-white/[0.1] rounded-xl text-white
                       focus:outline-none focus:border-white/30 transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) =>
                setFormData({ ...formData, order: parseInt(e.target.value) })
              }
              className="w-full px-4 py-3 bg-white/[0.05] border border-white/[0.1] rounded-xl text-white
                       focus:outline-none focus:border-white/30 transition-all"
            />
          </div>
        </div>

        <div>
          <label className="block text-white/60 text-sm mb-2">
            Description (Bullet Points)
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={descInput}
              onChange={(e) => setDescInput(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), addDescription())
              }
              className="flex-1 px-4 py-3 bg-white/[0.05] border border-white/[0.1] rounded-xl text-white
                       focus:outline-none focus:border-white/30 transition-all"
              placeholder="Add description point"
            />
            <button
              type="button"
              onClick={addDescription}
              className="px-6 py-3 bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 transition-all"
            >
              Add
            </button>
          </div>
          <div className="space-y-2">
            {formData.description.map((desc, index) => (
              <div
                key={index}
                className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg flex items-center justify-between gap-2"
              >
                <span className="flex-1">• {desc}</span>
                <button
                  type="button"
                  onClick={() => removeDescription(index)}
                  className="text-red-400 hover:text-red-300 text-xl font-bold"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-white/60 text-sm mb-2">
            Technologies
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={techInput}
              onChange={(e) => setTechInput(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), addTechnology())
              }
              className="flex-1 px-4 py-3 bg-white/[0.05] border border-white/[0.1] rounded-xl text-white
                       focus:outline-none focus:border-white/30 transition-all"
              placeholder="Add technology"
            />
            <button
              type="button"
              onClick={addTechnology}
              className="px-6 py-3 bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 transition-all"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.technologies.map((tech, index) => (
              <div
                key={index}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg flex items-center gap-2"
              >
                <span>{tech}</span>
                <button
                  type="button"
                  onClick={() => removeTechnology(index)}
                  className="text-red-400 hover:text-red-300"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-3 bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 
                     transition-all disabled:opacity-50"
          >
            {loading ? "Saving..." : editingId ? "Update" : "Create"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-3 bg-red-500/20 border border-red-500/30 rounded-xl hover:bg-red-500/30 transition-all"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {message.text && (
        <div
          className={`p-4 rounded-lg ${
            message.type === "success"
              ? "bg-green-500/10 border border-green-500/20 text-green-400"
              : "bg-red-500/10 border border-red-500/20 text-red-400"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* List */}
      <div className="space-y-4">
        <h3 className="text-xl font-light">Existing Experiences</h3>
        {experiences.map((exp) => (
          <div
            key={exp._id}
            className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-6 hover:border-white/15 transition-all"
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="text-lg font-medium">{exp.title}</h4>
                <p className="text-white/60">
                  {exp.company} • {exp.location} • {exp.period}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(exp)}
                  className="px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg hover:bg-blue-500/30 transition-all text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(exp._id)}
                  className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-all text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
            <div className="text-white/70 mb-3 space-y-1">
              {exp.description &&
                exp.description.map((desc, i) => <p key={i}>• {desc}</p>)}
            </div>
            <div className="flex flex-wrap gap-2">
              {exp.technologies.map((tech, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-white/10 border border-white/20 rounded-lg text-sm"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ExperienceAdmin;
